package com.example.hukakmp.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.example.hukakmp.data.FishingRepository
import com.example.hukakmp.data.PescadexCatalog
import com.example.hukakmp.getPlatform
import com.example.hukakmp.draft.PersistedReportDraft
import com.example.hukakmp.draft.ReportDraftPersistence
import com.example.hukakmp.draft.toNuevoParteInput
import com.example.hukakmp.draft.toPersistedDraft
import com.example.hukakmp.model.EspecieEntry
import com.example.hukakmp.model.FirebaseResult
import com.example.hukakmp.model.ModalidadPesca
import com.example.hukakmp.model.NuevoParteInput
import com.example.hukakmp.util.esFechaFutura
import com.example.hukakmp.util.esHoraFuturaHoy
import com.example.hukakmp.util.fechaHoyLocal
import com.example.hukakmp.util.horaAMinutos
import com.example.hukakmp.util.parsearFecha
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json

class ParteWizardViewModel(
    private val repository: FishingRepository,
    private val uid: String,
) : ViewModel() {
    enum class Paso { FECHA_HORA, MODALIDAD, UBICACION, ESPECIES, FOTO, NOTAS }

    data class WizardUiState(
        val paso: Paso = Paso.FECHA_HORA,
        val input: NuevoParteInput = NuevoParteInput(),
        val isSaving: Boolean = false,
        val error: String? = null,
        val guardadoExitoso: Boolean = false,
        val recuperadoDeBorrador: Boolean = false,
    )

    private val _uiState = MutableStateFlow(WizardUiState())
    val uiState: StateFlow<WizardUiState> = _uiState.asStateFlow()
    private val pasos = Paso.entries
    private val draftJson = Json { ignoreUnknownKeys = true }

    fun reiniciar() {
        ReportDraftPersistence.clear(uid)
        _uiState.value = WizardUiState()
    }

    fun prepararNuevoParte() {
        val actual = _uiState.value
        if (!actual.guardadoExitoso && actual.input.tieneDatosDeBorrador()) {
            _uiState.value = actual.copy(
                error = null,
                recuperadoDeBorrador = true,
            )
            return
        }

        val recuperado = cargarBorradorPersistido()
        if (recuperado == null) {
            _uiState.value = WizardUiState()
            return
        }

        val primerPasoIncompleto = pasos.firstOrNull { paso ->
            mensajeValidacion(paso, recuperado) != null
        } ?: Paso.NOTAS

        _uiState.value = WizardUiState(
            paso = primerPasoIncompleto,
            input = recuperado,
            recuperadoDeBorrador = true,
        )
    }

    fun descartarBorrador() {
        ReportDraftPersistence.clear(uid)
        _uiState.value = WizardUiState()
    }

    fun iniciarDesdeIdentificacion(
        foto: com.example.hukakmp.model.FotoSeleccionada,
        especies: List<String>,
    ) {
        _uiState.value = WizardUiState(
            paso = Paso.FECHA_HORA,
            input = NuevoParteInput(
                especies = especies
                    .filter { it.isNotBlank() }
                    .distinct()
                    .map { nombre -> EspecieEntry(especie = nombre, cantidad = 1) },
                fotos = listOf(foto),
            ),
        )
        persistirBorrador(_uiState.value.input)
    }

    fun iniciarDesdeContador(
        especies: List<com.example.hukakmp.counter.FishCounterEntry>,
    ) {
        _uiState.value = WizardUiState(
            paso = Paso.FECHA_HORA,
            input = NuevoParteInput(
                especies = especies.map {
                    EspecieEntry(
                        especie = it.name,
                        cantidad = it.count,
                        devueltos = it.returned.coerceIn(0, it.count),
                    )
                },
            ),
        )
        persistirBorrador(_uiState.value.input)
    }

    fun iniciarDesdeConversacion(input: NuevoParteInput) {
        val primerPasoIncompleto = pasos.firstOrNull { paso ->
            mensajeValidacion(paso, input) != null
        } ?: Paso.NOTAS

        _uiState.value = WizardUiState(
            paso = primerPasoIncompleto,
            input = input,
        )
        persistirBorrador(_uiState.value.input)
    }

    fun actualizar(transform: (NuevoParteInput) -> NuevoParteInput) {
        _uiState.update { it.copy(input = transform(it.input), error = null) }
        persistirBorrador(_uiState.value.input)
    }

    fun agregarEspecie() = actualizar { it.copy(especies = it.especies + EspecieEntry(cantidad = 1)) }

    fun actualizarEspecie(index: Int, especie: EspecieEntry) = actualizar { input ->
        if (index !in input.especies.indices) input
        else input.copy(especies = input.especies.toMutableList().also { it[index] = especie })
    }

    fun quitarEspecie(index: Int) = actualizar { input ->
        input.copy(especies = input.especies.filterIndexed { i, _ -> i != index })
    }

    fun pasoValido(paso: Paso, input: NuevoParteInput): Boolean = mensajeValidacion(paso, input) == null

    private fun mensajeValidacion(paso: Paso, input: NuevoParteInput): String? = when (paso) {
        Paso.FECHA_HORA -> validarFechaHora(input)
        Paso.MODALIDAD -> validarModalidad(input)
        Paso.UBICACION -> validarUbicacion(input)
        Paso.ESPECIES -> validarEspecies(input)
        Paso.FOTO -> validarFotos(input)
        Paso.NOTAS -> null
    }

    private fun validarFechaHora(input: NuevoParteInput): String? {
        if (input.fecha.isBlank()) return "Seleccioná la fecha de la jornada."
        val fecha = parsearFecha(input.fecha) ?: return "La fecha seleccionada no es válida."
        if (esFechaFutura(input.fecha)) return "La fecha no puede ser posterior a hoy."
        if (input.horaInicio.isBlank()) return "Seleccioná la hora de inicio."
        val inicio = horaAMinutos(input.horaInicio) ?: return "La hora de inicio no es válida."
        if (esHoraFuturaHoy(input.fecha, input.horaInicio)) return "La hora de inicio no puede estar en el futuro."
        if (input.horaFin.isBlank()) return "Seleccioná la hora de finalización."
        val fin = horaAMinutos(input.horaFin) ?: return "La hora de finalización no es válida."
        if (esHoraFuturaHoy(input.fecha, input.horaFin)) return "La hora de finalización no puede estar en el futuro."
        if (fecha == fechaHoyLocal() && fin < inicio) return "Para una jornada de hoy, la hora de fin no puede ser anterior a la hora de inicio."
        return null
    }

    private fun validarModalidad(input: NuevoParteInput): String? {
        val modalidad = input.modalidad ?: return "Seleccioná una modalidad de pesca."
        if (modalidad == ModalidadPesca.OTRA && input.modalidadOtra.trim().isEmpty()) return "Indicá cuál fue la modalidad de pesca."
        if (modalidad.usaCanas && (input.numeroCanas == null || input.numeroCanas <= 0)) return "Indicá cuántas cañas utilizaste."
        return null
    }

    private fun validarUbicacion(input: NuevoParteInput): String? {
        val lat = input.latitud.trim().replace(',', '.').toDoubleOrNull() ?: return "Seleccioná una ubicación válida para la jornada."
        val lon = input.longitud.trim().replace(',', '.').toDoubleOrNull() ?: return "Seleccioná una ubicación válida para la jornada."
        if (lat !in -90.0..90.0) return "La latitud debe estar entre -90 y 90."
        if (lon !in -180.0..180.0) return "La longitud debe estar entre -180 y 180."
        return null
    }

    private fun validarEspecies(input: NuevoParteInput): String? {
        val validarContraCatalogoArgentino = getPlatform().name == "Web"

        input.especies.forEachIndexed { index, especie ->
            val nombre = especie.especie.trim()
            if (nombre.isEmpty()) return "Completá el nombre de la especie ${index + 1}."

            if (validarContraCatalogoArgentino && !PescadexCatalog.isKnownSpecies(nombre)) {
                return "La especie \"$nombre\" no está en el catálogo de peces argentinos de Huka. Elegí una de las sugerencias."
            }

            if (especie.cantidad <= 0) return "La cantidad de la especie ${index + 1} debe ser mayor a cero."
            if (especie.devueltos < 0) return "La cantidad devuelta no puede ser negativa."
            if (especie.devueltos > especie.cantidad) return "Los ejemplares devueltos no pueden superar la cantidad capturada."
        }
        return null
    }

    private fun validarFotos(input: NuevoParteInput): String? {
        if (input.fotos.isEmpty()) return "Agregá al menos una fotografía de la jornada."
        if (input.fotos.size > MAX_FOTOS) return "Podés adjuntar hasta $MAX_FOTOS fotografías."
        if (input.fotos.any { it.bytes.isEmpty() }) return "Una de las fotografías no pudo leerse. Volvé a seleccionarla."
        if (input.fotos.any { it.bytes.size > MAX_FOTO_BYTES }) return "Cada fotografía debe pesar menos de 10 MB."
        return null
    }

    fun siguiente() {
        val actual = _uiState.value
        val error = mensajeValidacion(actual.paso, actual.input)
        if (error != null) {
            _uiState.update { it.copy(error = error) }
            return
        }
        val indice = pasos.indexOf(actual.paso)
        if (indice < pasos.lastIndex) _uiState.update { it.copy(paso = pasos[indice + 1], error = null) }
    }

    fun anterior() {
        val indice = pasos.indexOf(_uiState.value.paso)
        if (indice > 0) _uiState.update { it.copy(paso = pasos[indice - 1], error = null) }
    }

    fun guardar() {
        val actual = _uiState.value
        val invalido = pasos.firstOrNull { mensajeValidacion(it, actual.input) != null }
        if (invalido != null) {
            _uiState.update { it.copy(paso = invalido, error = mensajeValidacion(invalido, actual.input), isSaving = false) }
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isSaving = true, error = null) }
            when (val resultado = repository.guardarParte(_uiState.value.input)) {
                is FirebaseResult.Success -> {
                    ReportDraftPersistence.clear(uid)
                    _uiState.update {
                        it.copy(
                            isSaving = false,
                            guardadoExitoso = true,
                            recuperadoDeBorrador = false,
                        )
                    }
                }
                is FirebaseResult.Error -> _uiState.update { it.copy(isSaving = false, error = resultado.message) }
                is FirebaseResult.Loading -> Unit
            }
        }
    }

    private fun persistirBorrador(input: NuevoParteInput) {
        if (!input.tieneDatosDeBorrador()) {
            ReportDraftPersistence.clear(uid)
            return
        }

        runCatching {
            ReportDraftPersistence.save(
                uid,
                draftJson.encodeToString(
                    PersistedReportDraft.serializer(),
                    input.toPersistedDraft(),
                ),
            )
        }
    }

    private fun cargarBorradorPersistido(): NuevoParteInput? {
        val raw = ReportDraftPersistence.load(uid) ?: return null
        return runCatching {
            draftJson.decodeFromString(
                PersistedReportDraft.serializer(),
                raw,
            ).toNuevoParteInput()
        }.getOrNull()?.takeIf { it.tieneDatosDeBorrador() }
    }

    private fun NuevoParteInput.tieneDatosDeBorrador(): Boolean =
        fecha.isNotBlank() ||
            horaInicio.isNotBlank() ||
            horaFin.isNotBlank() ||
            modalidad != null ||
            modalidadOtra.isNotBlank() ||
            numeroCanas != null ||
            nombreLugar.isNotBlank() ||
            zona.isNotBlank() ||
            latitud.isNotBlank() ||
            longitud.isNotBlank() ||
            especies.isNotEmpty() ||
            fotos.isNotEmpty() ||
            observaciones.isNotBlank()

    companion object {
        const val MAX_FOTOS = 3
        const val MAX_FOTO_BYTES = 10 * 1024 * 1024
    }
}
